import {
  ORIGIN_REACT_MOBILE_APP,
  TYPE_SKIP,
  AUTHOR_TYPE_ACCOUNT,
  TELEMED_IN_PROGRESS,
  UBER_RIDE_REQUESTED,
  ASAP_VALUE,
  MARKETPLACE_DFW,
  BookingStatus,
} from '../../constants';
import { DAPI_HOST } from '../../config';
import { isNativeApp, isIosApp, isAndroidApp, getNativePlatform } from '../util/device';
import { apiPostDispatchable, apiPostJson } from './index';
import logger from '../../core/logger';
import { dateFormat } from '../../core/util/date';
import {
  analyticsIdentify,
  analyticsTagHotjarRecording,
  analyticsTrackEvent,
  analyticsTriggerHotjarRecording,
} from '../../core/analytics';
import { REVIEW_BOOKING_API_NEW_BOOKING } from '../../core/analytics/events';
import { getEligibilityId, getInsurerIdByCode } from '../../core/util/insurance';
import { isEmptyString } from '../../core/util/string';
import { isEmptyObject } from '../../core/util/object';
import { buildDapiUserProfileObject } from '../../core/dapi/userProfile';
import { getAccountId } from '../auth';
import {
  getGroupId,
  getLocationSpecialtyValue,
  isInDfwMarket,
  isPartOfMetroUrgentCareGroup,
  isSolvPartner,
  isBookable,
  isTelemedLocation,
} from '../../core/util/location';
import {
  fireMetroUrgentCareFacebookTracking,
  GA_EVENT,
  GA_EVENT_ACTION_BOOKING,
  GA_EVENT_CATEGORY_BOOKING,
  fireGoogleAnalytics,
  isConfiguredForGoogleAnalyticsTracking,
} from '../../core/analytics/trackingForPartners';
import { HOTJAR_TRACKING_LABEL } from '../tracking/hotjar/constants';
import { safeGet } from '../util/object';
import { INSURANCE_TYPES } from '../../constants';
import { isEmpty } from '../util/empty';
import { isViewable } from '../util/location';
import { isPersistedLogin } from '../session';
import { getTrackingUrl } from './tracking';
import { TrackingEntities, TrackingTypes } from '../../constants/dapi/tracking';

const getRescheduleBookingUrl = () => `${DAPI_HOST}/v1/registration/reschedule`;
const getAddBackupTimeUrl = () => `${DAPI_HOST}/v1/registration/add-backup-time`;
const getBookingById = bookingId => `${DAPI_HOST}/v1/bookings/${bookingId}`;
const getNewBookingUrl = () => `${DAPI_HOST}/v1/registration/bookings`;
const getPublicBookingById = bookingId => `${DAPI_HOST}/v1/public-bookings/${bookingId}`;

const buildBookingStatus = (newBooking, location) => {
  if (location.isOpaque) {
    return BookingStatus.PENDING;
  }

  return BookingStatus.RESERVED;
};

const buildZipCode = searchPreferences => {
  if (!searchPreferences || !searchPreferences.location) {
    return '';
  }

  if (searchPreferences.location.label && searchPreferences.location.label.match(/\d{5}/)) {
    return searchPreferences.location.label.replace(/^.*(\d{5}).*$/, '$1');
  }

  return searchPreferences.location.searchZipCode;
};

const normalizePhone = phone => {
  let sanitizedPhone = phone.replace(/\D/g, '');

  // if phone length more than 10, it's possible they are appending 1 to the phone number
  if (sanitizedPhone.length > 10) {
    sanitizedPhone = sanitizedPhone.replace(/^1/, '');
  }

  if (sanitizedPhone.length === 10) {
    sanitizedPhone = `+1${sanitizedPhone}`;
  }

  return sanitizedPhone;
};

const reasonForVisitTransformer = reasonForVisit => {
  if (reasonForVisit instanceof Array) {
    return reasonForVisit.map(element => element.label).join(', ');
  }

  return typeof reasonForVisit === 'string' ? reasonForVisit : '';
};

const getInsurerKey = insurance =>
  insurance.insuranceType === INSURANCE_TYPES.dental ? 'marketplace_insurers_id' : 'insurer_id';

const getInsurerId = (newbookingInsuranceObj, urgentCareInsurers) => {
  const insuranceType = safeGet(newbookingInsuranceObj)('insuranceType');
  if (isEmpty(insuranceType) || insuranceType === INSURANCE_TYPES.medical) {
    return (
      newbookingInsuranceObj.insurerId ||
      getInsurerIdByCode(newbookingInsuranceObj.insurerCode, urgentCareInsurers)
    );
  }

  return newbookingInsuranceObj.marketplace_insurers_id;
};

const buildBookingPostData = props => {
  const {
    newBooking,
    location,
    npi,
    searchPreferences,
    eligibilityCheck,
    tracking,
    insurers,
    position,
    trackingPropertiesReferrer,
  } = props;
  const getInsurerType = insurance => {
    // If insurerType was set through the booking flow use it
    // As as safety net, if that's not the case default to skip
    if (!isEmptyObject(insurance.insurerType)) {
      return insurance.insurerType;
    }

    return TYPE_SKIP;
  };

  const { booking, profile, insurance } = newBooking;
  const status = buildBookingStatus(newBooking, location);
  const zipCode = buildZipCode(searchPreferences);
  const phone = normalizePhone(profile.phone);
  const insurerIdKey = getInsurerKey(insurance);
  const insurerId = getInsurerId(insurance, insurers);

  const postData = {
    account: {
      phone,
      email: profile.email,
      first_name: profile.firstName,
      last_name: profile.lastName,
      address: profile.address,
      city: profile.city,
      state: profile.stateCode,
      zip_code: profile.zipCode,
      tos_consent: !!profile.tosConsent || !!profile.bookingTosConsent,
      is_ios_app_user: isIosApp(),
      is_android_app_user: isAndroidApp(),
    },

    user_profile: buildDapiUserProfileObject(profile),
    booking: {
      first_name: profile.firstName,
      last_name: profile.lastName,
      email: profile.email,
      phone,
      reason_for_visit: reasonForVisitTransformer(booking.reasonForVisit),
      origin: booking.origin || ORIGIN_REACT_MOBILE_APP,
      location_id: booking.locationId || location.id,
      appointment_date: dateFormat(
        booking.appointmentTime ? booking.appointmentTime : profile.appointmentTime,
        'isoUtcDateTime'
      ),
      preferred_time: booking.appointmentType === ASAP_VALUE ? ASAP_VALUE : null,
      zip_code: zipCode,
      status,
      is_logged_in: props.isLoggedIn,
      is_persistent_login: props.isLoggedIn && isPersistedLogin(),

      // DEPRECATED
      // Search parameter, not really booking data.
      birth_date: profile.birthDate,
      insurer_type: getInsurerType(insurance),
      uber_ride_status: profile.uberRequest === UBER_RIDE_REQUESTED ? profile.uberRequest : null,
      is_existing_patient: profile.isExistingPatient,
      is_direct_telemed: profile.isDirectTelemed,
      notes: booking.notes,
    },

    tracking,
  };

  let referrerDomain = null;
  if (trackingPropertiesReferrer) {
    try {
      referrerDomain = new URL(trackingPropertiesReferrer).hostname;
    } catch (e) {
      /* continue regardless of error */
    }
  }
  if (!isEmptyString(referrerDomain)) {
    postData.booking.referrer_domain = referrerDomain;
  }

  const nativePlatform = getNativePlatform();
  if (nativePlatform) {
    const utmData = `${nativePlatform}_app`;
    postData.tracking = {
      ...tracking,
      campaign: {
        ...(tracking && tracking.campaign),
        utm_source: utmData,
        utm_campaign: utmData,
        utm_medium: utmData,
      },
    };
  }

  postData.insurance_profile = {
    [insurerIdKey]: insurerId,
    insurer_name: insurance.insurerName,
    insurer_type: insurance.insurerType,
    insurance_type: insurance.insuranceType,
    member_code: insurance.memberCode,
  };

  if (getAccountId()) {
    postData.booking.author_type = AUTHOR_TYPE_ACCOUNT;
    postData.booking.author_id = getAccountId();
  }

  if (!isEmptyString(insurance.firstName) || !isEmptyString(profile.firstName)) {
    postData.insurance_profile.first_name = isEmptyString(insurance.firstName)
      ? profile.firstName
      : insurance.firstName;
  }

  if (!isEmptyString(insurance.lastName) || !isEmptyString(profile.lastName)) {
    postData.insurance_profile.last_name = isEmptyString(insurance.lastName)
      ? profile.lastName
      : insurance.lastName;
  }

  if (!isEmptyString(insurance.birthDate)) {
    postData.insurance_profile.birth_date = insurance.birthDate;
  }

  if (
    !isEmptyObject(position) &&
    !isEmptyString(position.result) &&
    !isEmptyString(position.result.latitude) &&
    !isEmptyString(position.result.longitude)
  ) {
    postData.booking.lat_long = `(${position.result.latitude},${position.result.longitude})`;
  }

  if (
    !isEmptyObject(searchPreferences) &&
    !isEmptyObject(searchPreferences.location) &&
    !isEmptyString(searchPreferences.location.latitude) &&
    !isEmptyString(searchPreferences.location.longitude)
  ) {
    const searchLat = searchPreferences.location.latitude;
    const searchLng = searchPreferences.location.longitude;
    postData.booking.search_lat_long = `(${searchLat},${searchLng})`;
  }

  if (!isEmptyString(insurance.cardFront)) {
    postData.insurance_profile.card_front_id = insurance.cardFront;
  }

  if (!isEmptyString(insurance.cardBack)) {
    postData.insurance_profile.card_back_id = insurance.cardBack;
  }

  const eligibilityId = getEligibilityId(newBooking, location, npi, eligibilityCheck);
  if (eligibilityId !== null) {
    postData.eligibility_requests = { eligibility_requests_id: eligibilityId };
  }

  if (insurance.insuranceProfileId) {
    postData.booking.insurance_profile_id = insurance.insuranceProfileId;
  }

  if (profile.userProfileId) {
    postData.booking.user_profile_id = profile.userProfileId;
  }

  if (booking.notes) {
    postData.booking.notes = booking.notes;
  }

  if (isTelemedLocation(location)) {
    postData.booking.telemed_status = TELEMED_IN_PROGRESS;
  }

  return postData;
};

const postBookingDispatchable = (dispatch, props, onSuccess, onFailure) => {
  const postData = buildBookingPostData(props);
  const handleBookingSuccess = response => {
    if (typeof response !== 'undefined') {
      const {
        location: { id, market, platformType },
        abTestGroup,
        trackingPropertiesSessionId,
        trackingPropertiesReferrer,
      } = props;
      const eventProperties = {
        booking_id: response.booking_id,
        location_id: id,
        is_solv_partner: isSolvPartner(props.location),
        is_bookable: isBookable(props.location),
        is_viewable: isViewable(props.location),
        market,
        platform_type: platformType,
        srpAbTestGroup: abTestGroup,
        trackingPropertiesSessionId,
      };

      if (getGroupId(props.location)) {
        eventProperties.group_id = getGroupId(props.location);
      }

      analyticsIdentify(response.account_id, postData);
      analyticsTrackEvent(REVIEW_BOOKING_API_NEW_BOOKING, eventProperties);
      if (trackingPropertiesReferrer) {
        apiPostJson(
          getTrackingUrl(
            TrackingEntities.BOOKINGS,
            response.booking_id,
            TrackingTypes.INFO_REFERRER
          ),
          {
            referrer_url: trackingPropertiesReferrer,
          }
        );
      }
      const locationBookingSpecialty = getLocationSpecialtyValue(props.location);
      const hotjarTags = [
        HOTJAR_TRACKING_LABEL.booking,
        `${HOTJAR_TRACKING_LABEL.booking}_${locationBookingSpecialty}`,
      ];
      if (isInDfwMarket(props.location)) {
        hotjarTags.push(MARKETPLACE_DFW);
        analyticsTriggerHotjarRecording(MARKETPLACE_DFW);
      }
      analyticsTagHotjarRecording(hotjarTags);

      const location = props.location;
      if (isConfiguredForGoogleAnalyticsTracking(location)) {
        fireGoogleAnalytics(location, GA_EVENT, GA_EVENT_ACTION_BOOKING, GA_EVENT_CATEGORY_BOOKING);
      }

      if (isPartOfMetroUrgentCareGroup(location)) {
        fireMetroUrgentCareFacebookTracking();
      }
    }

    return onSuccess(response);
  };

  logger.debug('Submitting booking');
  logger.debug(postData);
  dispatch(apiPostDispatchable(getNewBookingUrl(), postData, handleBookingSuccess, onFailure));
};

export {
  getAddBackupTimeUrl,
  getNewBookingUrl,
  buildBookingPostData,
  postBookingDispatchable,
  getBookingById,
  getRescheduleBookingUrl,
  getPublicBookingById,
  normalizePhone,
};
